import { cn } from 'cn';
import { type PropsWithChildren, type ReactNode } from 'react';

import { slugify } from '@vestigas/utils';

type HeadingRowProps = PropsWithChildren<{
  readonly action?: ReactNode;
  readonly heading: string;
  readonly headingClassName?: string;
  readonly icon?: ReactNode;
  readonly id?: string;
  readonly variant?: 'h1' | 'h2' | 'h3' | 'h4';
}> &
  ComponentStyling;

/**
 * A heading row with optional additional functionality such as a CTA button.
 *
 * @component
 * @param {ReactNode} action - The action element to be rendered on the right side of the heading row.
 * @param {ReactNode} children - The additional content to be rendered below the heading row.
 * @param {string} className - The CSS class name to be applied to the heading row container.
 * @param {string} heading - The text to be displayed as the heading.
 * @param {string} headingClassName - The CSS class name to be applied to the heading element.
 * @param {ReactNode} icon - The icon element to be displayed before the heading text.
 * @param {string} id - The HTML id attribute for the heading row container.
 * @param {CSSProperties} style - The inline CSS styles to be applied to the heading row container.
 * @param {string} [variant='h2'] - The variant of the heading element ('h1', 'h2', 'h3', etc.).
 *
 * @example
 * <HeadingRow
 *   action={<Button onClick={handleAction}>Action</Button>}
 *   heading="Heading Text"
 *   variant="h1"
 * >
 *   <p>Additional content</p>
 * </HeadingRow>
 *
 */
export const HeadingRow = ({
  action,
  children,
  className,
  heading,
  headingClassName,
  icon,
  id,
  style,
  variant = 'h2',
}: HeadingRowProps) => {
  const HeadingElement = variant;

  return (
    <header
      className={cn(
        '',
        {
          'pl-1': variant === 'h1',
        },
        className,
      )}
      id={id ?? slugify(heading)}
      style={style}
    >
      <div
        className={cn(
          'group/heading-row flex flex-wrap items-center gap-4 lg:flex-nowrap',
          {
            'py-8': variant === 'h1',
          },
        )}
      >
        <div className="flex max-w-full flex-1 items-center gap-4 text-ellipsis group-has-[[data-overflow-actions]]/heading-row:max-w-2/3 group-has-[[data-overflow-actions]]/heading-row:min-w-0 group-has-[[data-overflow-actions]]/heading-row:flex-initial">
          <HeadingElement
            className={cn(
              'text-tigaText-default order-1 line-clamp-1 flex items-center tracking-tight text-balance',
              {
                'gap-2 text-2xl': variant === 'h2',
                'gap-2 text-base': variant === 'h4',
                'gap-2 text-xl': variant === 'h3',
                'gap-4 text-4xl': variant === 'h1',
              },
              headingClassName,
            )}
          >
            {icon ? <span>{icon}</span> : null}
            <span className="w-full text-center">{heading}</span>
          </HeadingElement>
        </div>
        {Boolean(children) && (
          <div
            className={cn(
              'order-4 flex w-full flex-2 basis-full items-center lg:order-2 lg:w-auto lg:basis-0',
            )}
          >
            {children}
          </div>
        )}
        {Boolean(action) && (
          <div
            className={cn(
              'order-3 flex basis-0 items-center justify-end gap-x-4 has-[[data-overflow-actions]]:min-w-0 has-[[data-overflow-actions]]:flex-1',
              {
                'flex-1': children,
                'flex-0': children === undefined,
              },
            )}
          >
            {action}
          </div>
        )}
      </div>
    </header>
  );
};
